import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Building2, Calendar, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent, CardHeader } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import type { MyInvitation } from '@/entities/invitation'

type InvitationCardProps = {
  invitation: MyInvitation
  onDecline: (id: number) => void
  isPending: boolean
}

export function InvitationCard({ invitation, onDecline, isPending }: InvitationCardProps) {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)

  const invitedDate = new Date(invitation.invitedAt).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })

  const requiredCreds = invitation.requiredCredentials ?? []
  const requiredCount = requiredCreds.length

  const handleAccept = () => {
    navigate({
      to: '/invitations/$id/accept',
      params: { id: invitation.id.toString() },
    })
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-ink truncate">{invitation.accountName}</p>
              <Badge variant="secondary" className="mt-1">{invitation.employeeType}</Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2 text-xs text-ink-3">
          <Calendar className="h-3.5 w-3.5" />
          <span>Invited on {invitedDate}</span>
        </div>
        {invitation.message && (
          <p className="text-sm text-ink-2 italic border-l-2 border-line pl-3">
            "{invitation.message}"
          </p>
        )}

        {requiredCount > 0 && (
          <div className="rounded-md border border-line bg-surface-mute">
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="flex w-full items-center justify-between px-3 py-2 text-left"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-ink-2">
                <ShieldCheck className="h-4 w-4" />
                Required credentials ({requiredCount})
              </span>
              {expanded ? (
                <ChevronUp className="h-4 w-4 text-ink-3" />
              ) : (
                <ChevronDown className="h-4 w-4 text-ink-3" />
              )}
            </button>
            {expanded && (
              <ul className="space-y-1.5 border-t border-line px-3 py-2">
                {requiredCreds.map((rc) => (
                  <li
                    key={rc.id}
                    className="flex items-center justify-between gap-2 text-sm text-ink-2"
                  >
                    <span>{rc.name}</span>
                    {rc.isRequired && (
                      <Badge tone="warn" className="text-[10px]">
                        Required
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="flex items-center gap-2 pt-2">
          <Button size="sm" onClick={handleAccept} disabled={isPending}>
            Accept
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDecline(invitation.id)}
            disabled={isPending}
          >
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
